import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  catchError,
  filter,
  finalize,
  first,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

import { RumAnalyticsService } from '@tfs/datadog-rum-utils';
import { NotificationsHubClient } from '@tfs/notifications';
import {
  ToolImportJobApi,
  ToolDataImportModuleDataContractsToolImportJobDtoDto as ToolImportJob,
  ToolDataImportModuleDataContractsToolImportJobStatusDto as JobStatus,
  ToolDataImportModuleDataContractsToolImportJobTypeDto as JobType,
  ToolDataImportModuleDataContractsWebCatalogToolImportToolDetailsRequestDtoDto as WebCatalogToolImportToolDetailsRequest,
} from '@tfs/tool-library-library-api';

import { isToolImportJobStatusChangeEvent } from '../models/tool-import-events.model';

const TERMINAL_STATUSES = new Set<string>([
  JobStatus.Completed,
  JobStatus.Failed,
  JobStatus.Removed,
]);

@Injectable({ providedIn: 'root' })
export class ToolImportStatusService {
  private readonly toolImportJobApi = inject(ToolImportJobApi);
  private readonly notificationsHub = inject(NotificationsHubClient);
  private readonly snackBar = inject(MatSnackBar);
  private readonly rum = inject(RumAnalyticsService);

  readonly activeJob = signal<ToolImportJob | null>(null);
  readonly isLoading = signal(false);
  readonly isImportRunning = computed(() => this.activeJob() !== null);
  readonly currentJobType = computed(() => this.activeJob()?.importType);
  readonly isImportingDemoData = computed(() => this.currentJobType() === JobType.DemoData);

  /** Emits a job once the status feed observes it reaching a terminal status. */
  private readonly terminalJob$ = new Subject<ToolImportJob>();
  private lastProcessedTerminalJobId: string | null = null;

  private readonly initialStatusSync$ = this.refreshCurrentJob().pipe(
    takeUntilDestroyed(),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  readonly firstPollComplete = firstValueFrom(this.initialStatusSync$);

  constructor() {
    this.notificationsHub.notifications$
      .pipe(
        filter(isToolImportJobStatusChangeEvent),
        switchMap(() => this.refreshCurrentJob({ showSnackbar: true })),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  startDemoDataImport(): Observable<boolean> {
    this.isLoading.set(true);
    const existingJob = this.activeJob();
    const job$ = existingJob
      ? of(existingJob)
      : this.toolImportJobApi
          .requestDemoDataToolImportJob()
          .pipe(tap(job => this.activeJob.set(job)));

    return job$.pipe(
      switchMap(job =>
        this.terminalJob$.pipe(
          filter(terminal => terminal.id === job.id),
          first(),
          map(terminal => terminal.status === JobStatus.Completed)
        )
      ),
      finalize(() => this.isLoading.set(false))
    );
  }

  /** Queues a Web Catalog import for the given tools and marks the job as the active import. */
  requestWebCatalogImport(
    toolImportRequests: WebCatalogToolImportToolDetailsRequest[]
  ): Observable<void> {
    return this.toolImportJobApi.requestWebCatalogToolImportJob({ tools: toolImportRequests }).pipe(
      tap(importJob => {
        this.activeJob.set(importJob);
        this.snackBar.open('Import of tool item started', 'Close', {
          duration: 3000,
          panelClass: 'info',
        });
      }),
      map(() => undefined)
    );
  }

  private refreshCurrentJob({ showSnackbar = false } = {}): Observable<void> {
    return this.toolImportJobApi.getActiveToolImportJob().pipe(
      switchMap(job => this.applyJobState(job, showSnackbar)),
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 404) {
          this.activeJob.set(null);
          return of(undefined);
        }

        return this.toolImportJobApi.getRecentToolImportJob().pipe(
          switchMap(job => this.applyJobState(job, showSnackbar)),
          catchError(() => {
            this.activeJob.set(null);
            return of(undefined);
          })
        );
      })
    );
  }

  private applyJobState(job: ToolImportJob | null, showSnackbar = false): Observable<void> {
    if (job && TERMINAL_STATUSES.has(job.status)) {
      const isNewTerminalJob = job.id !== this.lastProcessedTerminalJobId;
      this.lastProcessedTerminalJobId = job.id;

      if (isNewTerminalJob) {
        this.terminalJob$.next(job);
      }

      this.activeJob.set(null);

      if (showSnackbar && isNewTerminalJob) {
        if (job.importType === JobType.WebCatalog) {
          this.rum.trackAction('tool_item.bulk_add', {
            itemCount: job.overallItemsCount ?? 0,
            success: job.status === JobStatus.Completed,
          });
        }

        if (job.status === JobStatus.Completed) {
          this.snackBar.open('Tool data import completed successfully.', 'Close', {
            duration: 5000,
            panelClass: 'success',
          });
        } else if (job.status === JobStatus.Failed) {
          this.snackBar.open("We couldn't import your tool data. Please try again.", 'Close', {
            duration: 5000,
            panelClass: 'error',
          });
        }
      }

      return of(undefined);
    }

    this.activeJob.set(job);
    return of(undefined);
  }
}
